import { LinkIcon } from '@heroicons/react/24/outline';
import { EyeIcon, EyeSlashIcon, XCircleIcon } from '@heroicons/react/20/solid';

import { useWidgetStore, WidgetID } from '@/widget-store';

import { Badge } from '@/components/badge';
import { Button } from '@/components/button';
import { DragDrop } from '@/components/drag-drop';
import { useEffect, useState } from 'react';
import { FullscreenToggle } from '@/components/fullscreen-toggle';
import clsx from 'clsx';
import { createPortal } from 'react-dom';
import { useSearchParams } from 'react-router-dom';

interface WidgetProps {
  id: WidgetID;
  children: React.ReactNode;
  title?: string;
  toolbar?: React.ReactNode;
  scrollable?: boolean;
  connectable?: boolean;
  hideable?: boolean;
  minHeight?: number;
  onVisibilityChange?: (isVisible: boolean) => void;
  initialVisible?: boolean;
}

export function Widget({
  id,
  children,
  title,
  toolbar,
  scrollable = true,
  connectable = false,
  hideable = false,
  minHeight = 0,
  onVisibilityChange,
  initialVisible = hideable ? false : true,
}: WidgetProps) {
  const registerWidget = useWidgetStore((state) => state.registerWidget);
  const connect = useWidgetStore((state) => state.connect);
  const removeConnection = useWidgetStore((state) => state.removeConnection);
  const widget = useWidgetStore((state) => state.widgets[id]);
  const [isFullscreen, setFullscreen] = useState(false);
  const [isVisible, setIsVisible] = useState(initialVisible);
  const [searchParams] = useSearchParams();

  useEffect(() => {
    registerWidget(id);
  }, []);

  useEffect(() => {
    const connectTo = searchParams.get('connectTo');

    if (!connectTo || !widget) {
      return;
    }

    const hasConnection = widget.emitters.includes(connectTo);

    if (hasConnection) {
      return;
    }

    const targetWidget = useWidgetStore.getState().widgets[connectTo];
    if (!targetWidget) {
      console.log(`Target widget ${connectTo} not found in store`);
      return;
    }

    connect(connectTo, id);
  }, [searchParams, widget, id, connect]);

  useEffect(() => {
    setIsVisible(initialVisible);
  }, [initialVisible]);

  if (!widget) {
    return null;
  }

  function handleDrop(data: Record<string, unknown>) {
    if (
      data &&
      typeof data.value === 'object' &&
      data.value !== null &&
      'widgetId' in data.value
    ) {
      const widgetId = (data.value as { widgetId: string }).widgetId;
      connect(widgetId, id);
    }
  }

  function handleRemoveClick(emitterId: string) {
    removeConnection(emitterId, id);
  }

  function handleFullscreenToggle() {
    setFullscreen((prev) => !prev);
  }

  const className = clsx(
    'divide-y divide-gray-200 rounded-lg bg-white dark:bg-gray-900 dark:divide-none shadow flex-1 flex flex-col min-w-0',
    isFullscreen && 'fixed inset-0 z-[9999]',
    scrollable && 'overflow-hidden',
    !isVisible && 'hidden'
  );

  const contentClassName = clsx(
    'px-4 py-5 sm:p-6 flex-1',
    scrollable ? 'overflow-y-auto overflow-x-hidden' : ''
  );

  function handleVisibilityToggle() {
    const newVisibility = !isVisible;
    setIsVisible(newVisibility);
    onVisibilityChange?.(newVisibility);
  }

  const controlButtonClassName = clsx(
    'inline-flex gap-1 items-center rounded-md bg-white px-2 py-1 text-xs  shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50',
    isVisible ? 'text-gray-900' : 'text-gray-400'
  );

  const controlButton = hideable
    ? createPortal(
        <button
          onClick={handleVisibilityToggle}
          className={controlButtonClassName}
        >
          {isVisible ? (
            <>
              <EyeIcon
                className="h-3.5 w-3.5"
                aria-hidden="true"
                focusable="false"
                aria-label="Hide"
              />
              {title}
            </>
          ) : (
            <>
              <EyeSlashIcon
                className="h-3.5 w-3.5"
                aria-hidden="true"
                focusable="false"
                aria-label="Show"
              />
              {title}
            </>
          )}
        </button>,
        document.getElementById('page-actions') || document.body
      )
    : null;

  return (
    <>
      {controlButton}
      <div className={className}>
        {toolbar ? (
          <div className="px-4 py-5 sm:px-6">
            <div className="flex flex-wrap items-center justify-between sm:flex-nowrap">
              <h2 className="text-lg font-medium leading-6 text-gray-900 dark:text-gray-50">
                {title}
              </h2>
              <div className="flex-shrink-0 flex gap-2 items-center">
                <div
                  id={`widget-actions-${id}`}
                  className="flex items-center gap-3 mr-6"
                />
                {connectable ? (
                  <DragDrop
                    allowedTypes={['link']}
                    onDrop={(data) => handleDrop(data)}
                    key="drag-drop"
                    className="flex items-center"
                  >
                    {() => {
                      if (widget?.emitters.length === 0) {
                        return (
                          <Button>
                            <LinkIcon className="h-4 w-4" aria-hidden="true" />
                          </Button>
                        );
                      }

                      return widget?.emitters.map((emitter) => (
                        <Badge key={emitter} className="my-[0.3125rem]">
                          Connected to: {emitter}
                          <button
                            title="Remove connection"
                            className="-mr-2.5 ml-1 w-5 h-5 inline-flex items-center justify-center rounded-full text-green-800 hover:text-red-600 transition-colors"
                            onClick={() => {
                              handleRemoveClick(emitter);
                            }}
                          >
                            <XCircleIcon className="w-5 h-5" />
                          </button>
                        </Badge>
                      ));
                    }}
                  </DragDrop>
                ) : null}
                {toolbar}
                <FullscreenToggle onClick={handleFullscreenToggle} />
              </div>
            </div>
          </div>
        ) : (
          <div className="px-4 py-5 sm:px-6">
            <div className="flex flex-wrap items-center justify-between sm:flex-nowrap">
              <h2 className="text-lg font-medium leading-6 text-gray-900 dark:text-gray-50">
                {title}
              </h2>
              <div className="flex-shrink-0 flex gap-2">
                <div
                  id={`widget-actions-${id}`}
                  className="flex items-center gap-3 mr-6"
                />
                <DragDrop
                  allowedTypes={['link']}
                  onDrop={(data) => handleDrop(data)}
                  key="drag-drop"
                >
                  {() => {
                    if (widget?.emitters.length === 0) {
                      return (
                        <Button>
                          <LinkIcon className="h-4 w-4" aria-hidden="true" />
                        </Button>
                      );
                    }

                    return widget?.emitters.map((emitter) => (
                      <Badge key={emitter} className="my-[0.3125rem]">
                        Connected to: {emitter}
                        <button
                          title="Remove connection"
                          className="-mr-2.5 ml-1 w-5 h-5 inline-flex items-center justify-center rounded-full text-green-800 hover:text-red-600 transition-colors"
                          onClick={() => {
                            handleRemoveClick(emitter);
                          }}
                        >
                          <XCircleIcon className="w-5 h-5" />
                        </button>
                      </Badge>
                    ));
                  }}
                </DragDrop>
                <FullscreenToggle onClick={handleFullscreenToggle} />
              </div>
            </div>
          </div>
        )}
        <div
          className={contentClassName}
          style={{ minHeight: `${minHeight}px` }}
        >
          {children}
        </div>
      </div>
    </>
  );
}
